import { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

/**
 * Mobile drawer state. Mirrors `body[data-sidenav-mobile-open]`, which
 * `base.html` styles to slide the panel and fade in `#sidenav-mobile-backdrop`.
 * Auto-closes on route change, Escape, and backdrop click.
 */
export const useMobileSidenav = () => {
	const [isOpen, setIsOpen] = useState(false);
	const location = useLocation();

	const close = useCallback(() => setIsOpen(false), []);
	const open = useCallback(() => setIsOpen(true), []);
	const toggle = useCallback(() => setIsOpen((prev) => !prev), []);

	useEffect(() => {
		if (typeof document === "undefined") return;
		if (document.body.dataset.layout !== "sidenav") return;
		document.body.dataset.sidenavMobileOpen = isOpen ? "true" : "false";
	}, [isOpen]);

	useEffect(() => {
		setIsOpen(false);
	}, [location.pathname]);

	useEffect(() => {
		if (!isOpen || typeof document === "undefined") return undefined;

		const onKey = (event: KeyboardEvent) => {
			if (event.key === "Escape") close();
		};

		const backdrop = document.getElementById("sidenav-mobile-backdrop");
		const onBackdropClick = () => close();

		document.addEventListener("keydown", onKey);
		backdrop?.addEventListener("click", onBackdropClick);

		return () => {
			document.removeEventListener("keydown", onKey);
			backdrop?.removeEventListener("click", onBackdropClick);
		};
	}, [isOpen, close]);

	return { isOpen, open, close, toggle };
};
